import Alert from "@/components/core/alert";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { useModal } from "@/components/core/modals/use-modal.ts";
import Tooltip from "@/components/core/tooltip";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { caseRecordQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import { useCaseRecordStatusMutation } from "@/features/case-record/api/queries/case-records-query";
import {
	useRaffleMutation,
	useRafflingMutation,
} from "@/features/case-record/api/queries/raffle-query";
import raffleService from "@/features/case-record/api/services/raffle-service";
import { courtBranchesQueryOptions } from "@/features/court-detail/api/queries/court-branches-query";
import CourtBranchmodal from "@/features/court-detail/component/court-branch-modal";
import {
	IconAlertCircle,
	IconClockHour2,
	IconPencil,
} from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export const Route = createFileRoute("/_backoffice/raffle_/create_/$caseId")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(caseRecordQueryOptions(params.caseId));
	},
	component: DashboardIndex,
});

function DashboardIndex() {
	const caseRecordStatusMutation = useCaseRecordStatusMutation();
	const raffleMutation = useRaffleMutation();
	const rafflingMutation = useRafflingMutation();

	const modal = useModal();
	const params = Route.useParams();
	const search = Route.useSearch();
	const userQuery = useSuspenseQuery(caseRecordQueryOptions(params.caseId));
	const data = userQuery.data;

	const [hasRaffle, setHasRaffle] = useState(
		data.status !== "for_raffling" &&
			data.status !== "deferred_raffling" &&
			data.status !== "inhibit" &&
			!data.has_jdr,
	);

	const branchQuery = useSuspenseQuery(
		courtBranchesQueryOptions({
			...search,
			court_station_id: data.court_station_id,
			case_id: data.id,
		}),
	);

	const branches = branchQuery.data?.data;

	const form = useForm({
		defaultValues: {
			id: 1,
			case_id: data.id,
			judge: "",
			branch: "",
		},
	});

	const openAddModal = (data) => {
		modal.openModal({
			allowClose: false,
			title: "Update Branch Details",
			description: "branch details",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: <CourtBranchmodal data={data} />,
		});
	};

	// const [winner, setWinner] = useState<string | null>(null)
	const [spinning, setSpinning] = useState<boolean>(false);
	const [selectedJudge, setSelectedJudge] = useState<string | null>(null);
	const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
	const drawTime = 3000;
	const [countdown, setCountDown] = useState<number>(0);

	const drawWinner = async () => {
		setCountDown(drawTime);
		if (branches.length === 0) return;

		setCountDown(drawTime);
		setSpinning(true);
		const selectedBranch = await rafflingMutation.mutateAsync({
			case_id: data.id,
			station_id: data.court_station_id,
		});
		setSelectedBranch(selectedBranch.data);
		console.log(selectedBranch.data);
		setTimeout(async () => {
			const randomIndex = Math.floor(Math.random() * branches.length);
			const selectedWinner = branches[randomIndex];
			if (!data.has_jdr) {
				await updateStatus(selectedBranch.data);
			}
			await saveRaffle(selectedBranch.data);
			setSpinning(false);
			setSelectedJudge(selectedBranch.data);
		}, drawTime);
	};

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (spinning) {
			interval = setInterval(() => {
				const randomIndex = Math.floor(Math.random() * branches.length);

				setSelectedJudge(branches[randomIndex]);
				setCountDown((prev) => prev - 100);
			}, 100);
		}
		return () => clearInterval(interval);
	}, [spinning, branches]);

	const saveRaffle = async (selected) => {
		await raffleMutation.mutateAsync({
			case_id: data.id,
			branch_id: selected.id,
			id: null,
			branch: null,
			author: null,
			case: null,
			created_at: null,
		});
	};

	const updateStatus = async (selected) => {
		await caseRecordStatusMutation.mutateAsync({
			id: data.id,
			status: "branch_receiving",
			branch_id: selected.id,
		});
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await raffleService.findByIdCaseId(data.id);
				setSelectedJudge(response.branch);
			} catch (error) {
				console.error("Error fetching case stage chart data:", error);
			}
		};
		if (hasRaffle) {
			fetchData();
		}
	}, []);

	return (
		<Content>
			<Header title={`${data.case_number}`} crumb="Raffle" />
			<div className="flex flex-col w-full max-w-base p-6 gap-4">
				{!data.has_jdr && (
					<Alert
						type="info"
						allowClose={true}
						title="Docketing Successfull"
						icon={<IconAlertCircle size={20} stroke={2} />}
					>
						<span>
							Case Number : <b>{data.case_number}</b> has been assigned. You may
							now begin the Branch Raffling.
						</span>
					</Alert>
				)}

				<div className="flex flex-col justify-center items-center w-full min-h-[240px] p-4 gap-4 rounded-md border border-border/50">
					<div className="inline-flex flex-row items-center w-auto gap-1 px-4 py-2 rounded-md bg-slate-50 text-foreground/70">
						<IconClockHour2 size={20} stroke={2} />
						<span className="font-semibold text-base">
							Time Remaining:{" "}
							{Math.floor(countdown / 1000) < 0
								? 0
								: Math.floor(countdown / 1000)}
						</span>
					</div>
					<span className="font-bold text-5xl text-foreground-dark">
						{selectedJudge?.name
							? selectedJudge?.name
							: data.has_jdr
								? "Select JDR Branch"
								: "Select Branch"}
					</span>
					<Button
						className=""
						onClick={drawWinner}
						disabled={!!spinning || !!selectedJudge?.name || hasRaffle}
					>
						{spinning ? "Raffling..." : "Begin Raffle"}
					</Button>
				</div>
				{selectedJudge?.name && !spinning && (
					<Alert
						type="info"
						allowClose={true}
						title="Notice"
						icon={<IconAlertCircle size={20} stroke={2} />}
					>
						System notification has been sent to judge.
					</Alert>
				)}
				<div className="flex w-full rounded-md border border-border/50 overflow-hidden">
					<table className="w-full">
						<tbody>
							{params.caseId === "0" && (
								<tr className="border-b border-border/50 last:border-0">
									<td className="w-[1%] p-4 whitespace-nowrap border-r border-border/50 bg-slate-50 text-base text-foreground">
										Purpose
									</td>
									<td className="text-base font-semibold font-foreground-dark">
										<Form {...form}>
											<FormField
												control={form.control}
												name="username"
												render={({ field }) => (
													<Input
														className="p-4 text-foreground-dark border border-white focus:border-focus-border outline-none focus:ring-2 focus:ring-inset focus:ring-focus-ring/30"
														autoComplete="off"
														placeholder="Using this for..."
														{...field}
													/>
												)}
											/>
										</Form>
									</td>
								</tr>
							)}

							<tr className="border-b border-border/50 last:border-0">
								<td className="w-[1%] p-4 whitespace-nowrap border-r border-border/50 bg-slate-50 text-base text-foreground">
									Presiding Judge
								</td>
								<td className="p-4 text-base font-semibold font-foreground-dark">
									{selectedJudge?.name && !spinning ? (
										<div className="">
											{selectedJudge?.judge}
											<Tooltip content="Update Presiding Judge" side="top">
												<Button
													className="translate-y-[3px] ml-1"
													variant="link"
													size="xs"
													onClick={() => {
														openAddModal(selectedJudge);
													}}
												>
													<IconPencil size={16} stroke={2} />
												</Button>
											</Tooltip>
										</div>
									) : (
										"--"
									)}
								</td>
							</tr>
							<tr className="border-b border-border/50 last:border-0">
								<td className="w-[1%] p-4 whitespace-nowrap border-r border-border/50 bg-slate-50 text-base text-foreground">
									Branch
								</td>
								<td className="p-4 text-base font-semibold font-foreground-dark">
									{selectedJudge?.name && !spinning ? (
										<div className="">{selectedJudge?.name}</div>
									) : (
										"--"
									)}
								</td>
							</tr>
							<tr className="border-b border-border/50 last:border-0">
								<td className="w-[1%] p-4 whitespace-nowrap border-r border-border/50 bg-slate-50 text-base text-foreground">
									Address
								</td>
								<td className="p-4 text-base font-semibold font-foreground-dark">
									{selectedJudge?.name && !spinning ? (
										<div className="">
											{selectedJudge?.address}
											<Tooltip content="Update Address" side="top">
												<Button
													className="translate-y-[3px] ml-1"
													variant="link"
													size="xs"
													onClick={() => {
														openAddModal(selectedJudge);
													}}
												>
													<IconPencil size={16} stroke={2} />
												</Button>
											</Tooltip>
										</div>
									) : (
										"--"
									)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</Content>
	);
}
